import type { AgeClassification } from "../../../../api/client/types/age-classification.types";
import type { SwimmerItemSchema } from "./RegisterSwimmerForm.types";

/* ---------- Domain Types ---------- */

export interface BatchDomainErrors {
  global?: string;
  swimmers: {
    missingResponsible?: string;
    invalidResponsible?: string;
  }[];
}

export interface BatchDomainState {
  errors: BatchDomainErrors;
  canSubmit: boolean;
}

/* ---------- Helpers ---------- */

export const findAgeClassification = (
  ageClassifications: AgeClassification[],
  id: string
) => ageClassifications.find((ac) => ac.id.toString() === id) ?? null;

export const isAdultAC = (ageClassificationName: string): boolean =>
  ageClassificationName?.toLowerCase().trim().includes("mayor de 18") ?? false;

export const isMinorAC = (ageClassificationName: string): boolean =>
  ageClassificationName?.toLowerCase().trim().includes("menor de 12") ?? false;

export const isTeenAC = (ageClassificationName: string): boolean =>
  (ageClassificationName?.toLowerCase().trim().includes("12") &&
    ageClassificationName?.toLowerCase().trim().includes("18")) ??
  false;

/**
 * Returns form indices of adults and minors under 12.
 * Used for auto-assigning the single adult as responsible when there is only one adult.
 */
export function getAdultAndMinorUnder12Indices(
  swimmers: SwimmerItemSchema[],
  ageClassifications: AgeClassification[]
): { adultIndices: number[]; minorUnder12Indices: number[] } {
  const ageClassificationsMap = ageClassifications.reduce<
    Record<string, AgeClassification>
  >((acc, ac) => {
    acc[ac.id.toString()] = ac;
    return acc;
  }, {});
  const getAC = (id: string) => ageClassificationsMap[id] ?? null;

  const adultIndices: number[] = [];
  const minorUnder12Indices: number[] = [];

  swimmers.forEach((s, idx) => {
    const ac = getAC(s.age_classification_id?.toString() ?? "");
    if (!ac) return;
    if (isAdultAC(ac.name)) adultIndices.push(idx);
    else if (isMinorAC(ac.name)) minorUnder12Indices.push(idx);
  });

  return { adultIndices, minorUnder12Indices };
}

/* ---------- Core Domain Evaluation ---------- */

/**
 * Pure validation function that mirrors backend rules:
 *
 * 1) Minors (<12) must have responsibleIdx (adult).
 * 2) Teens / adults must NOT have responsibleIdx.
 * 3) If responsibleIdx is set:
 *    - must reference an existing swimmer
 *    - must reference an adult
 *    - must not self-reference
 * 4) If any child exists, there must be at least one adult.
 *
 * @param swimmers - Array of swimmers in the form (includes initial swimmer at index 0 if present)
 * @param ageClassifications - Available age classifications
 */
export function evaluateBatchDomain(
  swimmers: SwimmerItemSchema[],
  ageClassifications: AgeClassification[]
): BatchDomainState {
  const ageClassificationsMap = ageClassifications.reduce<
    Record<string, AgeClassification>
  >((acc, ac) => {
    acc[ac.id.toString()] = ac;
    return acc;
  }, {});

  // SAFE GETTER
  const getAC = (id: string) => ageClassificationsMap[id] ?? null;

  // Count adults in the form (includes initial swimmer at index 0 if it's an adult)
  const adults = swimmers.filter((s) => {
    const ac = getAC(s.age_classification_id?.toString());
    return ac ? isAdultAC(ac.name) : false;
  });

  const minors = swimmers.filter((s) => {
    const ac = getAC(s.age_classification_id?.toString());
    return ac ? isMinorAC(ac.name) : false;
  });

  const errors: BatchDomainErrors = {
    swimmers: swimmers.map(() => ({})),
  };

  swimmers.forEach((swimmer, idx) => {
    const responsibleIdx =
      swimmer.responsibleIdx?.trim() === ""
        ? null
        : Number(swimmer.responsibleIdx?.trim());

    const ac = getAC(swimmer.age_classification_id?.toString());

    // If swimmer has NO age classification selected, skip all rules
    if (!ac) return;

    /* --------------------------- CHILD RULE --------------------------- */
    if (isMinorAC(ac.name)) {
      if (
        responsibleIdx === null ||
        responsibleIdx === undefined ||
        isNaN(responsibleIdx)
      ) {
        errors.swimmers[idx].missingResponsible =
          "Debe seleccionar un adulto responsable.";
        return;
      }

      // Validate responsibleIdx reference
      const target = swimmers[responsibleIdx];
      const targetAC = getAC(target?.age_classification_id?.toString() ?? "");

      if (
        !target ||
        !targetAC ||
        !isAdultAC(targetAC.name) ||
        responsibleIdx === idx
      ) {
        errors.swimmers[idx].invalidResponsible =
          "El responsable asignado no es válido.";
      }
      return;
    }

    /* ----------------------- TEEN / ADULT RULE ------------------------ */
    if (isTeenAC(ac.name) || isAdultAC(ac.name)) {
      if (
        responsibleIdx !== null &&
        responsibleIdx !== undefined &&
        !isNaN(responsibleIdx)
      ) {
        errors.swimmers[idx].invalidResponsible =
          "Los mayores de 12 años no pueden tener responsable.";
      }
      return;
    }
  });

  /* ------------------------------ GLOBAL RULE ------------------------------ */
  // If there are minors, there must be at least one adult
  if (minors.length > 0 && adults.length === 0) {
    errors.global =
      "Debe existir al menos un adulto (Mayor de 18 años) si hay menores de 12 años.";
  }

  const hasPerSwimmerErrors = errors.swimmers.some(
    (e) => e.missingResponsible || e.invalidResponsible
  );

  return {
    errors,
    canSubmit: !errors.global && !hasPerSwimmerErrors,
  };
}
